/**
 * O envio pelo recebimento (E133.F1/F2, GUS-106, ADR-0089). Espelha `apps/api/types/recebimentos.py`.
 *
 * Cada lote de arquivos escolhidos abre um recebimento, e cada arquivo vai para ele. O "Tentar
 * novamente" reusa o mesmo recebimento: o servidor reconhece os bytes que já entraram nele, e
 * retomar não duplica. O servidor decide pelos bytes o que o arquivo é: pacote, documento ou
 * arquivo não examinado, que fica guardado e marcado com o motivo.
 */
import type { AxiosInstance } from 'axios';

import type { ManifestoNoReciboData } from '@/lib/abertura_do_caso';
import {
  classifyDocumentError,
  DocumentError,
  type SourceDocumentKindEnviado,
  type SourceDocumentRecordData,
  UPLOAD_TIMEOUT_MS,
} from '@/lib/case_source_documents';
import { nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';

/** O teto de um arquivo: acima dele a plataforma não guarda, e a linha diz o limite. */
export const MAX_BYTES_DO_ARQUIVO = 50 * 1024 * 1024;

export type MotivoNaoExaminado =
  | 'formato_fora_do_exame'
  | 'profundidade_excedida'
  | 'arquivo_vazio'
  | 'acima_do_limite'
  | 'pacote_ilegivel'
  | 'pacote_cifrado'
  | 'pacote_acima_do_limite'
  | 'pacote_vazio';

export interface RecebimentoData {
  id: string;
  case_id: string;
  usuario_id: string;
  criado_em: string;
  /** E177.F3: a CCB declarada do bloco; ausente ou nulo sem CCB. */
  ccb_da_abertura_id?: string | null;
}

export interface ArquivoNaoExaminadoData {
  id: string;
  case_id: string;
  recebimento_id: string;
  pacote_document_id: string | null;
  usuario_id: string;
  nome: string;
  motivo: MotivoNaoExaminado;
  tamanho_bytes: number;
  content_sha256: string;
  criado_em: string;
  removido_em?: string | null;
  /** Só na resposta do envio: os mesmos bytes já estavam guardados (a retentativa). */
  copia_identica?: boolean;
}

export interface CopiaIdenticaData {
  caminho: string;
  documento_id: string;
}

/** O que um arquivo enviado deixou no caso. */
export interface EntradaNoRecebimentoData {
  recebimento_id: string;
  pacote_document_id: string | null;
  documentos: SourceDocumentRecordData[];
  nao_examinados: ArquivoNaoExaminadoData[];
  copias_identicas: CopiaIdenticaData[];
}

export interface EnvioAoRecebimento {
  file: File;
  kind: SourceDocumentKindEnviado;
  onProgress?: (percent: number) => void;
}

/** E177.F4 (ADR-0130 §2.2): com `ccbId`, o bloco da CCB declarada — tudo o que entrar nele tem a
 * CCB de origem. */
export async function abrirRecebimento(
  client: AxiosInstance,
  caseId: string,
  ccbId?: string,
): Promise<RecebimentoData> {
  try {
    const { data } = await client.post<RecebimentoData>(
      `/casos/${caseId}/recebimentos`,
      undefined,
      ccbId !== undefined ? { params: { ccb_id: ccbId } } : undefined,
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function enviarAoRecebimento(
  client: AxiosInstance,
  caseId: string,
  recebimentoId: string,
  envio: EnvioAoRecebimento,
): Promise<EntradaNoRecebimentoData> {
  const form = new FormData();
  form.append('file', envio.file);
  try {
    const { data } = await client.post<EntradaNoRecebimentoData>(
      `/casos/${caseId}/recebimentos/${recebimentoId}/arquivos`,
      form,
      {
        params: { kind: envio.kind },
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: UPLOAD_TIMEOUT_MS,
        onUploadProgress: (evt) => {
          if (envio.onProgress && evt.total != null && evt.total > 0) {
            envio.onProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      },
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

export async function removerNaoExaminado(
  client: AxiosInstance,
  caseId: string,
  itemId: string,
): Promise<void> {
  try {
    await client.delete(`/casos/${caseId}/nao-examinados/${itemId}`);
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/**
 * E199.F1 (POC, 31/10): a remoção que o servidor não encontra (404) já aconteceu — o clique
 * repetido, ou a tela que ainda mostrava a linha, não é falha. Quem remove trata como removido
 * e relê o recibo, em vez de acusar "Não foi possível remover".
 */
export function jaRemovido(err: unknown): boolean {
  return err instanceof DocumentError && err.reason === 'document_not_found';
}

/** E135.F1 (ADR-0091): sela o recebimento com o manifesto da plataforma; repetir devolve o mesmo. */
export async function concluirRecebimento(
  client: AxiosInstance,
  caseId: string,
  recebimentoId: string,
): Promise<ManifestoNoReciboData> {
  try {
    const { data } = await client.post<ManifestoNoReciboData>(
      `/casos/${caseId}/recebimentos/${recebimentoId}/conclusao`,
    );
    return data;
  } catch (err) {
    throw classifyDocumentError(err);
  }
}

/** E135.F1: os bytes exatos do manifesto selado, para quem confere o SHA-256 por fora. */
export async function baixarManifesto(
  client: AxiosInstance,
  caseId: string,
  recebimentoId: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(
      `/casos/${caseId}/recebimentos/${recebimentoId}/manifesto`,
      { responseType: 'blob' },
    );
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(
        resposta.headers['content-disposition'],
        `manifesto-do-recebimento-${recebimentoId.slice(0, 8)}.json`,
      ),
    };
  } catch (err) {
    throw classifyDocumentError(err);
  }
}
